import "./StepCardVariableSelector.scss"

import classNames from "classnames"
import React, { useCallback } from "react"

import { useGlobalTranslation, useTooltip } from "../../../utils/hook"
import { handleStepTagChanges } from "../../../utils/step/step-utils"
import StepCardVariableInput from "../../atoms/StepCardVariableInput"

const StepCardVariableSelector = ({
  allSupportedTags = [],
  allSelectedTags = [],
  inputRef,
  value = "",
  allValues = null,
  onMessageChange,
  additionalVariables = [],
  disabled = false,
  type = "",
  activeMessageIndex = null,
  changeSelectedNodeData,
  hideReplacement,
  onHandleVariableReplacementChange,
  expand = false,
  textOnlyEmails,
  setState,
}) => {
  const { tt } = useGlobalTranslation()
  const stepCardVariableSelectorClass = classNames("step-card-variable-selector-wrapper")

  const allSelectedTagsPerABTest = allSelectedTags[activeMessageIndex] || []

  const handleVariableReplacementChange = useCallback(
    (e, tag) => {
      if (onHandleVariableReplacementChange) {
        onHandleVariableReplacementChange({ target: { id: tag, value: e.target.value } })
      } else {
        handleStepTagChanges(
          { target: { value: e.target.value, id: tag } },
          activeMessageIndex,
          allSelectedTags,
          newSelectedTags => {
            changeSelectedNodeData({ tagInfos: newSelectedTags })
          },
        )
      }
    },
    [
      onHandleVariableReplacementChange,
      handleStepTagChanges,
      activeMessageIndex,
      allSelectedTags,
      changeSelectedNodeData,
    ],
  )

  const onVariableSelect = async tag => {
    let newTag = `{{${tag}}}`

    let insertPosition = inputRef.current?.selectionStart

    const selection = inputRef.current?.editor?.selection

    if (insertPosition === undefined) {
      insertPosition = selection?.savedRange?.index || 0
    }

    if (type === "html") {
      if (!textOnlyEmails && inputRef.current && selection) {
        insertPosition = selection.savedRange.index
      } else if (inputRef.current && inputRef.current.selectionStart !== undefined) {
        // For textOnlyEmails use current cursor position
        insertPosition = inputRef.current.selectionStart
      } else {
        // If cursor position is not available, use end of text
        insertPosition = value.length
      }
    }

    // Add space if needed at cursor position
    if (insertPosition > 0 && value.length > 0) {
      const charBeforeCursor = value.charAt(insertPosition - 1)
      if (charBeforeCursor !== " " && charBeforeCursor !== "\n") {
        newTag = ` ${newTag}`
      }
    }

    const tagAlreadyExists = allSelectedTagsPerABTest.find(selTag => selTag.tag === tag)
    if (!tagAlreadyExists) {
      handleVariableReplacementChange({ target: { value: "", id: tag } }, tag)
    }

    if (type === "html") {
      if (!textOnlyEmails) {
        const editor = inputRef.current.getEditor()

        // Insert text at cursor position
        editor.insertText(insertPosition, newTag, "user")

        // Set cursor after inserted tag
        const newCursorPosition = insertPosition + newTag.length
        editor.setSelection(newCursorPosition, newCursorPosition)
      } else {
        // Use insertPosition to add tags at cursor position
        const newValue = [value.slice(0, insertPosition), newTag, value.slice(insertPosition)].join(
          "",
        )
        const newCursorPosition = insertPosition + newTag.length

        setState(newValue)
        await onMessageChange(newValue)

        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
          inputRef.current.focus()
        }
      }
    } else {
      const newValue = [value.slice(0, insertPosition), newTag, value.slice(insertPosition)].join(
        "",
      )
      await onMessageChange(newValue)

      const newFocusPosition = insertPosition + newTag.length

      if (inputRef.current) {
        inputRef.current.selectionStart = newFocusPosition
        inputRef.current.selectionEnd = newFocusPosition
        inputRef.current.focus() // Todo(sb): Unhandled Rejection (TypeError): Cannot read property 'focus' of null
      }
    }
  }

  const [tooltip, setTooltip, closeTooltip] = useTooltip(10, 0)

  const setHover = (e, hoverTop, hoverLeft, description) => {
    setTooltip({
      event: e,
      text: <span>{description}</span>,
      topPos: hoverTop,
      leftPos: hoverLeft,
      customStyle: {
        width: 150,
      },
    })
  }

  const renderAllSupportedTags = () => {
    return (
      <>
        {allSupportedTags.map(({ tag, description }) => {
          const inputValue = allSelectedTagsPerABTest.find(connectTag => connectTag.tag === tag)

          if (disabled && (!inputValue || !inputValue.replaceWith)) {
            return null
          }

          return (
            <div
              key={tag}
              onMouseEnter={e => {
                const top = 80
                const left = -15
                return !expand && setHover(e, top, left, description)
              }}
              onMouseLeave={closeTooltip}
            >
              <StepCardVariableInput
                onVariableSelect={onVariableSelect}
                tt={tt}
                tag={tag}
                hideReplacement={hideReplacement}
                allValues={allValues}
                value={value}
                activeMessageIndex={activeMessageIndex}
                handleChange={handleVariableReplacementChange}
                inputValue={inputValue?.replaceWith}
                disabledButton={disabled}
                parseTag
              />
            </div>
          )
        })}
      </>
    )
  }

  const renderAdditionalVariables = () => {
    return (
      <>
        {additionalVariables.map(({ tag, description }) => {
          const inputValue = allSelectedTagsPerABTest.find(connectTag => connectTag.tag === tag)

          if (disabled && (!inputValue || !inputValue.replaceWith)) {
            return null
          }
          const top = 100
          const left = -15

          return (
            <div
              onMouseEnter={e => !expand && setHover(e, top, left, description)}
              onMouseLeave={closeTooltip}
            >
              <StepCardVariableInput
                key={tag}
                tt={tt}
                onVariableSelect={onVariableSelect}
                tag={tag}
                hideReplacement={hideReplacement}
                allValues={allValues}
                value={value}
                activeMessageIndex={activeMessageIndex}
                handleChange={handleVariableReplacementChange}
                inputValue={inputValue?.replaceWith}
                disabledButton={disabled}
              />
            </div>
          )
        })}
      </>
    )
  }

  return (
    <div className={stepCardVariableSelectorClass}>
      {tooltip}
      <div className="button-variable-container">{renderAllSupportedTags()}</div>
      {additionalVariables.length > 0 ? <hr style={{ marginTop: "1rem" }} /> : null}
      <div className="button-variable-container additional-variables">
        {renderAdditionalVariables()}
      </div>
    </div>
  )
}

// StepCardVariableSelector.propTypes = {
//   allSupportedTags: PropTypes.instanceOf(Array),
//   additionalVariables: PropTypes.instanceOf(Array),
//   allSelectedTags: PropTypes.instanceOf(Array),
//   value: PropTypes.string,
//   disabled: PropTypes.bool,
//   type: PropTypes.string,
//   activeMessageIndex: PropTypes.number,
//   changeSelectedNodeData: PropTypes.func.isRequired,
//   onHandleVariableReplacementChange: PropTypes.func,
//   allValues: PropTypes.instanceOf(Array),
//   expand: PropTypes.bool,
// }

export default React.memo(StepCardVariableSelector)
